import { signIn } from 'next-auth/react'
import {
  isSampleLocked,
  LOCKED_CORRECTION_HEADER,
  requestLockedCorrection,
} from './locked-correction'
import { getFreshSession } from './fresh-session'

// Resolve API URL, enforcing HTTPS when the page is served over HTTPS.
// NEXT_PUBLIC_* vars are baked at build time — if the image was built with an
// http:// URL but the site is served over https://, the browser will block the
// request as Mixed Content. This guard upgrades the scheme automatically.
//
// IMPORTANT: This function is called lazily per-request (not at module load).
// Evaluating at module load time would capture `window === undefined` during
// SSR, permanently caching the http:// URL for all subsequent client fetches.
function resolveApiUrl(): string {
  const raw = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    raw.startsWith('http://')
  ) {
    return raw.replace('http://', 'https://')
  }
  return raw
}

// Exported utility so other files (audit export, register) can get the
// correctly resolved URL without duplicating the HTTPS upgrade logic.
export function getApiUrl(): string {
  return resolveApiUrl()
}

const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

// Guard to prevent multiple parallel 401 responses from each triggering a
// separate signIn() redirect (which causes an infinite reload loop).
let isRedirecting = false

/**
 * A robust API client that handles authentication and base path resolution.
 * @param endpoint The API endpoint to call, e.g., '/api/claims' or '/api/samples'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.).
 */
// ── Custom API error class (preserves HTTP status + parsed body) ──────────────
// Using a proper subclass instead of attaching arbitrary properties to Error:
//   - TypeScript knows about .status and .body (typesafe)
//   - `instanceof Error` still returns true for existing catch blocks
//   - `instanceof ApiError && err.status === 409` works for specific handling
export class ApiError extends Error {
  status: number
  body: Record<string, unknown> | null

  constructor(
    message: string,
    status: number,
    body: Record<string, unknown> | null = null
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/**
 * Shared authenticated fetch. Attaches the bearer token, handles the
 * session-refresh error and 401 re-login redirect, and throws on non-OK
 * responses. Returns the raw Response so callers can read it as JSON or as a
 * Blob — this is the single auth path for every authenticated request.
 */
async function authedFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
  // Resolve lazily on each call — ensures window.location is available (browser, not SSR)
  const API_URL = resolveApiUrl()
  const session = await getFreshSession()

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  // Handle session errors that occurred during a token refresh attempt.
  if (session?.error === 'RefreshAccessTokenError') {
    if (!isRedirecting) {
      isRedirecting = true
      await signIn('keycloak')
    }
    throw new Error('Session expired. Please sign in again.')
  }

  // Construct the full URL: http://localhost:8001/app1/api/claims
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...options, headers })

  // If the backend returns a 401, it means the token is invalid or expired.
  // This can happen if the backend is restarted or the user's session is revoked.
  // We trigger a re-login to fix the session.
  if (response.status === 401) {
    if (!isRedirecting) {
      isRedirecting = true
      await signIn('keycloak')
    }
    throw new Error('Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: response.statusText,
    }))
    // Preserve the full error context — status code and parsed body —
    // so callers can distinguish 409 (duplicate) from 422 (validation), etc.
    // When detail is a JSON object (FastAPI structured errors), extract .message
    // or fall back gracefully. When detail is a plain string, use it directly.
    const message =
      typeof errorData.detail === 'string'
        ? errorData.detail
        : (errorData.detail?.message as string) ||
          (errorData.message as string) ||
          'An API error occurred.'
    // D46: a change to a sample already submitted to CMS. An MRM or PM gives a
    // reason in the warning dialog and the request is repeated with it, once.
    if (
      response.status === 423 &&
      isSampleLocked(errorData.detail) &&
      errorData.detail.can_correct &&
      !headers.has(LOCKED_CORRECTION_HEADER)
    ) {
      const reason = await requestLockedCorrection(errorData.detail)
      if (reason) {
        const retryHeaders = new Headers(options.headers || {})
        retryHeaders.set(LOCKED_CORRECTION_HEADER, encodeURIComponent(reason))
        return authedFetch(endpoint, { ...options, headers: retryHeaders })
      }
      throw new ApiError('Correction cancelled. Nothing was changed.', response.status, errorData.detail)
    }
    throw new ApiError(message, response.status, errorData.detail ?? errorData)
  }

  return response
}

async function apiClientFetch<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await authedFetch(endpoint, options)

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  post: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a POST request with multipart/form-data (FormData).
   * Does NOT set Content-Type header to allow browser to generate boundary.
   */
  postMultipart: <T = unknown>(endpoint: string, formData: FormData, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        ...options?.headers,
      },
      body: formData,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },

  getBlob: (endpoint: string, options?: RequestInit): Promise<Blob> => {
    return authedFetch(endpoint, { ...options, method: 'GET' }).then(r => r.blob())
  },

  /**
   * Perform a POST request and return the raw Response object.
   * Useful when response headers need to be inspected.
   */
  postRaw: (endpoint: string, data?: unknown, options?: RequestInit): Promise<Response> => {
    return authedFetch(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },
}

// Default export for backward compatibility
export default apiClientFetch
